import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ReactFlow, ReactFlowProvider, addEdge, useNodesState, useEdgesState, Controls, Background, Handle } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Layout, Input, Typography, Card, Button, List, message, Tag } from 'antd';

const { Sider, Content } = Layout;
const { Title } = Typography;

// Node Types
const nodeTypes = {
    message: ({ data }) => (
        <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, padding: 10, width: 150, textAlign: 'center' }}>
            <Handle type="target" position="top" style={{ background: '#555' }} />
            <strong>Message</strong>
            <p>{data.label || 'Send a message'}</p>
            <Handle type="source" position="bottom" style={{ background: '#555' }} />
        </div>
    ),
    question: ({ data }) => (
        <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, padding: 10, width: 150, textAlign: 'center' }}>
            <Handle type="target" position="top" style={{ background: '#555' }} />
            <strong>Question</strong>
            <p>{data.label || 'Ask a question'}</p>
            <Handle type="source" position="bottom" style={{ background: '#555' }} />
        </div>
    ),
};

// Draggable Node Item
const NodeItem = ({ type, label }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'node',
        item: { type },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    return (
        <Card
            ref={drag}
            style={{ marginBottom: 8, cursor: 'move', opacity: isDragging ? 0.5 : 1 }}
            bodyStyle={{ padding: 8 }}
        >
            {label}
        </Card>
    );
};

// Sidebar Component
const Sidebar = () => (
    <Sider width={200} style={{ background: '#f0f2f5', padding: 16 }}>
        <Title level={4} style={{ marginBottom: 16 }}>Node Palette</Title>
        <NodeItem type="message" label="Message Node" />
        <NodeItem type="question" label="Question Node" />
    </Sider>
);

// Property Editor Component
const PropertyEditor = ({ selectedNode, updateNode }) => {
    const [inputValue, setInputValue] = useState(selectedNode ? selectedNode.data.label : '');

    useEffect(() => {
        setInputValue(selectedNode ? selectedNode.data.label || '' : '');
    }, [selectedNode]);

    const handleChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (selectedNode) {
            updateNode(selectedNode.id, { label: newValue });
        }
    };

    if (!selectedNode) {
        return (
            <Sider width={200} style={{ background: '#f0f2f5', padding: 16 }}>
                <Title level={4}>Edit Node</Title>
                <p>Select a node to edit</p>
            </Sider>
        );
    }

    return (
        <Sider width={200} style={{ background: '#f0f2f5', padding: 16 }}>
            <Title level={4} style={{ marginBottom: 16 }}>Edit Node</Title>
            <Input
                value={inputValue}
                onChange={handleChange}
                placeholder="Node text"
                style={{ marginBottom: 8 }}
            />
        </Sider>
    );
};

// Preview Component
const Preview = ({ nodes, edges }) => {
    const [currentNode, setCurrentNode] = useState(nodes[0] || null);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');

    // Only reset if nodes are empty
    useEffect(() => {
        if (!nodes.length) {
            setCurrentNode(null);
            setMessages([]);
            setUserInput('');
        } else if (!currentNode || !nodes.find((node) => node.id === currentNode.id)) {
            setCurrentNode(nodes[0] || null);
            setMessages([]);
            setUserInput('');
        }
    }, [nodes, edges, currentNode]);

    const getValidResponses = () => {
        if (currentNode?.type === 'question') {
            return edges
                .filter((edge) => edge.source === currentNode.id && edge.label)
                .map((edge) => edge.label);
        }
        return [];
    };

    const handleNext = () => {
        if (!currentNode) {
            message.info('No nodes in the flow.');
            return;
        }

        if (currentNode.type === 'question') {
            const nextEdge = edges.find(
                (edge) => edge.source === currentNode.id && edge.label?.toLowerCase() === userInput.toLowerCase()
            );
            const nextNode = nextEdge ? nodes.find((node) => node.id === nextEdge.target) : null;
            if (nextNode) {
                setMessages((msgs) => [...msgs, `User: ${userInput}`, nextNode.data.label]);
                setCurrentNode(nextNode);
                setUserInput('');
            } else {
                message.info(`No matching response. Valid options: ${getValidResponses().join(', ') || 'none'}`);
            }
        } else {
            const nextEdge = edges.find((edge) => edge.source === currentNode.id);
            const nextNode = nextEdge ? nodes.find((node) => node.id === nextEdge.target) : null;
            if (nextNode) {
                setMessages((msgs) => [...msgs, nextNode.data.label]);
                setCurrentNode(nextNode);
                setUserInput('');
            } else {
                message.info('No further nodes in the flow.');
            }
        }
    };

    const handleReset = () => {
        setMessages([]);
        setCurrentNode(nodes[0] || null);
        setUserInput('');
    };

    const handleInputChange = (e) => {
        setUserInput(e.target.value);
    };

    const handleInputKeyPress = (e) => {
        if (e.key === 'Enter' && userInput) {
            handleNext();
        }
    };

    return (
        <Sider width={200} style={{ background: '#f0f2f5', padding: 16 }}>
            <Title level={4} style={{ marginBottom: 16 }}>Preview</Title>
            {currentNode ? (
                <>
                    <List
                        dataSource={[currentNode.data.label, ...messages]}
                        renderItem={(item) => <List.Item>{item}</List.Item>}
                        style={{ marginBottom: 16, maxHeight: '50vh', overflowY: 'auto' }}
                    />
                    {currentNode.type === 'question' && (
                        <>
                            <div style={{ marginBottom: 8 }}>
                                {getValidResponses().map((response) => (
                                    <Tag
                                        key={response}
                                        color="blue"
                                        style={{ cursor: 'pointer', marginRight: 4 }}
                                        onClick={() => {
                                            setUserInput(response);
                                            handleNext();
                                        }}
                                    >
                                        {response}
                                    </Tag>
                                ))}
                            </div>
                            <Input
                                value={userInput}
                                onChange={handleInputChange}
                                onKeyPress={handleInputKeyPress}
                                placeholder="Enter response"
                                style={{ marginBottom: 8 }}
                            />
                        </>
                    )}
                    <Button
                        type="primary"
                        onClick={handleNext}
                        style={{ marginRight: 8 }}
                        disabled={!currentNode || (currentNode.type === 'question' && !userInput)}
                    >
                        Next
                    </Button>
                    <Button onClick={handleReset}>Reset</Button>
                </>
            ) : (
                <p>No nodes to preview.</p>
            )}
        </Sider>
    );
};

// Canvas Component
const FlowCanvas = ({ nodes, setNodes, edges, setEdges, onNodesChange, onEdgesChange, selectedNode, setSelectedNode }) => {
    const reactFlowWrapper = useRef(null);
    let id = 0;
    const getId = () => `node_${id++}`;

    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'node',
        drop: (item, monitor) => {
            const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            const position = {
                x: clientOffset.x - reactFlowBounds.left,
                y: clientOffset.y - reactFlowBounds.top,
            };
            const newNode = {
                id: getId(),
                type: item.type,
                position,
                data: { label: item.type === 'message' ? 'Hello!' : 'What is your name?' },
            };
            setNodes((nds) => nds.concat(newNode));
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    }));

    const onConnect = useCallback(
        (params) => {
            // Add edge label for question nodes
            const sourceNode = nodes.find((node) => node.id === params.source);
            let edgeLabel;
            if (sourceNode?.type === 'question') {
                edgeLabel = prompt('Enter edge label (e.g., yes/no):') || '';
                if (!edgeLabel) {
                    message.warning('Edge label cannot be empty for question nodes.');
                    return; // Prevent adding edge if label is empty
                }
            }
            setEdges((eds) => addEdge({ ...params, type: 'smoothstep', label: edgeLabel }, eds));
        },
        [setEdges, nodes]
    );

    const onNodeClick = useCallback((event, node) => {
        setSelectedNode(node);
    }, [setSelectedNode]);

    return (
        <div ref={reactFlowWrapper} style={{ height: 'calc(100% - 64px)', border: isOver ? '2px dashed #1890ff' : '2px dashed #d9d9d9' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                ref={drop}
            >
                <Controls />
                <Background />
            </ReactFlow>
        </div>
    );
};

// Main BotBuilder Component
const BotBuilder = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNode, setSelectedNode] = useState(null);

    const updateNode = useCallback((id, data) => {
        setNodes((nds) =>
            nds.map((node) => (node.id === id ? { ...node, data: { ...node.data, ...data } } : node))
        );
    }, [setNodes]);

    const saveFlow = () => {
        const flow = { nodes, edges };
        localStorage.setItem('bot-flow', JSON.stringify(flow));
        message.success('Flow saved!');
    };

    const restoreFlow = () => {
        const flow = JSON.parse(localStorage.getItem('bot-flow'));
        if (flow) {
            setNodes(flow.nodes || []);
            setEdges(flow.edges || []);
            message.success('Flow restored!');
        } else {
            message.error('No saved flow found.');
        }
    };

    return (
        <ReactFlowProvider>
            <DndProvider backend={HTML5Backend}>
                <Layout style={{ height: '100vh' }}>
                    <Sidebar />
                    <Content>
                        <div style={{ padding: 16 }}>
                            <Button type="primary" onClick={saveFlow} style={{ marginRight: 8 }}>
                                Save Flow
                            </Button>
                            <Button onClick={restoreFlow}>
                                Restore Flow
                            </Button>
                        </div>
                        <FlowCanvas
                            nodes={nodes}
                            setNodes={setNodes}
                            edges={edges}
                            setEdges={setEdges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            selectedNode={selectedNode}
                            setSelectedNode={setSelectedNode}
                        />
                    </Content>
                    <PropertyEditor selectedNode={selectedNode} updateNode={updateNode} />
                    <Preview nodes={nodes} edges={edges} />
                </Layout>
            </DndProvider>
        </ReactFlowProvider>
    );
};

export default BotBuilder;